





























































import {
  defineComponent, ref, onMounted, Ref, computed, watchEffect,
} from '@vue/composition-api';
import { Map, Marker } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import store from '@/store/index';
import IPosition from '@/interfaces/IPosition';
import MapService from '@/services/MapService';
import RedrawService from '@/services/RedrawService';
import LayerSelector from '@/components/map/overlays/LayerSelector.vue';
import MapOverlay from '@/components/map/MapOverlay.vue';
import ArticlesOverlay from '@/components/map/overlays/ArticlesOverlay.vue';
import NotificationService from '@/services/NotificationService';

interface IHandleMapElement {
  mapElement: Ref<HTMLElement>,
  mapHandler: Ref<Map>,
  currentPosition: Ref<IPosition>,
  currentZoom: Ref<number>,
  userPositionMarker: Ref<Marker>,
}

const useHandleMapElement = (): IHandleMapElement => {
  const mapElement: Ref<HTMLElement> = ref(Object.create(HTMLElement.prototype, {}));
  const mapHandler: Ref<Map> = ref(Map.prototype);
  const currentPosition: Ref<IPosition> = computed(() => {
    const storePosition: IPosition = store.getters.getPosition;
    return {
      lat: storePosition.lat,
      lng: storePosition.lng,
    };
  });
  const currentZoom: Ref<number> = computed(() => {
    const storeZoom: number = store.getters.getZoomFactor;
    return storeZoom;
  });
  const userPositionMarker: Ref<Marker> = ref(Marker.prototype);

  onMounted(() => {
    mapHandler.value = new Map(mapElement.value, {
      zoomControl: false,
    });

    const mapService = new MapService(mapHandler.value);

    mapService.initializeMap();
    mapService.setPostition(currentPosition.value, 13);
    mapService.startPositionSetMode();
    mapService.setPositionFromGeolocation();

    store.commit('updateLeafletMapInstance', mapHandler.value);

    const redrawService = new RedrawService(mapService);

    store.dispatch('updateSettings')
      .then(redrawService.updateAndRedraw)
      .then(() => {
        NotificationService.success('Uruchomienie aplikacji', 'Pomyślnie zainicjalizowano mapę.');
      });

    // handler pulled outside of MapService to avoid
    // cicrular dependencies
    mapHandler.value.on('zoomend', () => {
      redrawService.redrawMap();
    });

    // each minute refresh data on map
    // necessary because rest api
    // does not provide websockets
    setInterval(() => {
      redrawService.updateAndRedraw();
    }, 60 * 1000);
  });

  return {
    mapElement,
    mapHandler,
    currentPosition,
    currentZoom,
    userPositionMarker,
  };
};

interface IMapControls{
  selectLocationMode: Ref<boolean>,
  handleClickGpsPositionUpdate: () => void,
  gpsPositionModal: Ref<boolean>,
}

const useMapControls = (handleMapElement: IHandleMapElement): IMapControls => {
  const { mapHandler } = handleMapElement;
  const selectLocationMode = computed(() => store.state.modes.selectLocation);
  const gpsPositionModal: Ref<boolean> = ref(false);

  watchEffect(() => {
    if (selectLocationMode.value === true) {
      const mapService = new MapService(mapHandler.value);
      mapService.startPositionSetMode();
    }
  });

  const handleClickGpsPositionUpdate = () => {
    const mapService = new MapService(mapHandler.value);
    mapService.setPositionFromGeolocation()
      .catch(() => {
        gpsPositionModal.value = true;
      });
  };

  return {
    selectLocationMode,
    handleClickGpsPositionUpdate,
    gpsPositionModal,
  };
};

interface IMapWindowSetup{
  mapElement: Ref<HTMLElement>,
  mapHandler: Ref<Map>,
  currentPosition: Ref<IPosition>,
  currentZoom: Ref<number>,
  userPositionMarker: Ref<Marker>,
  selectLocationMode: Ref<boolean>,
  handleClickGpsPositionUpdate: () => void,
  gpsPositionModal: Ref<boolean>,
}

const MapWindow: object = defineComponent({
  name: 'MapWindow',
  components: {
    MapOverlay,
    LayerSelector,
    ArticlesOverlay,
  },
  setup(): IMapWindowSetup {
    const useHandleMapElementData: IHandleMapElement = useHandleMapElement();

    return {
      ...useHandleMapElementData,
      ...useMapControls(useHandleMapElementData),
    };
  },
});

export default MapWindow;

